import { useState } from "react";
import { Swords, Info, Loader2, Wand2, Trash2, PlusCircle, ChevronRight, RotateCcw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  BACKEND_URL, COMPETITOR_ZONES, COMPETITOR_HORIZONS, THREAT_STYLE, HORIZON_STYLE, COMPETITOR_FRAMEWORK, uid,
} from "./constants";

function AiWand({ onClick, busy, label = "AI draft", testid, title }) {
  return (
    <Button size="sm" variant="ghost" onClick={onClick} disabled={busy}
      className="h-7 px-2 text-amber-600 hover:bg-amber-50" title={title || label} data-testid={testid}>
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
      <span className="ml-1 text-xs">{label}</span>
    </Button>
  );
}

const emptyCompetitor = (zone = "direct") => ({
  id: uid(), zone, horizon: "now", company: "", target: "", modality: "", stage: "",
  why_matters: "", why_win: "", why_fail: "", threat_level: "medium",
});

export default function CompetitorLandscapeTab({ landscape, setLandscape, persist }) {
  const [editing, setEditing] = useState(null);           // competitor object being edited
  const [addingToZone, setAddingToZone] = useState(null); // zone id when clicking "Add" empty
  const [suggesting, setSuggesting] = useState(false);
  const [diffBusy, setDiffBusy] = useState(false);

  const competitors = landscape.competitors || [];
  const claim = landscape.differentiation_claim || "";

  const updateLandscape = (patch) => setLandscape({ ...landscape, ...patch });

  const openNew = (zone) => { setAddingToZone(zone); setEditing(emptyCompetitor(zone)); };
  const openEdit = (c) => { setAddingToZone(null); setEditing({ ...c }); };
  const removeCompetitor = (id) => {
    updateLandscape({ competitors: competitors.filter((c) => c.id !== id) });
    persist();
  };
  const saveCompetitor = () => {
    if (!editing || !editing.company.trim()) { toast.error("Company name is required"); return; }
    const clean = { ...editing, company: editing.company.trim() };
    const exists = competitors.find((c) => c.id === clean.id);
    const next = exists ? competitors.map((c) => (c.id === clean.id ? clean : c)) : [...competitors, clean];
    updateLandscape({ competitors: next });
    setEditing(null);
    setAddingToZone(null);
    persist();
  };

  const suggestAll = async () => {
    setSuggesting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/market/suggest-competitors`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ existing: competitors.map((c) => c.company) }),
      });
      if (res.ok) {
        const d = await res.json();
        const added = (d.competitors || []).map((c) => ({ ...c, id: uid() }));
        if (added.length) {
          updateLandscape({ competitors: [...competitors, ...added] });
          persist();
          toast.success(`Added ${added.length} AI-suggested competitors`);
        } else toast.info("No suggestions returned");
      } else toast.error("Failed to get suggestions");
    } catch (e) { toast.error("Failed to get suggestions"); }
    setSuggesting(false);
  };

  const suggestDifferentiation = async () => {
    setDiffBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/strategy/market/suggest-differentiation`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ currentClaim: claim, competitors }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d.claim) { updateLandscape({ differentiation_claim: d.claim }); persist(); toast.success("AI differentiation draft ready"); }
        else toast.info("No draft returned");
      } else toast.error("Failed to draft claim");
    } catch (e) { toast.error("Failed to draft claim"); }
    setDiffBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="competitor-landscape-tab">
      {/* Framework hint */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2 text-slate-800"><Swords className="w-4 h-4 text-teal-600" /> Competitor Landscape</CardTitle>
            <AiWand onClick={suggestAll} busy={suggesting} label="AI populate all zones" testid="cl-suggest-all-btn" title="Auto-populate the four zones from your profile, deck & market news" />
          </div>
          <p className="text-xs text-slate-500">Move beyond &ldquo;we have a few competitors&rdquo; — think about the future landscape too, what the field may look like by the time you&apos;re ready for partnering, investment or clinical proof.</p>
        </CardHeader>
        <CardContent>
          <details className="text-xs text-slate-600">
            <summary className="cursor-pointer text-teal-700 hover:underline flex items-center gap-1"><Info className="w-3.5 h-3.5" /> Framework to think through</summary>
            <ul className="mt-2 space-y-1 pl-4 list-disc marker:text-teal-400">
              {COMPETITOR_FRAMEWORK.map((q, i) => <li key={i}>{q}</li>)}
            </ul>
          </details>
        </CardContent>
      </Card>

      {/* Timeline header */}
      <div className="flex items-center justify-between px-1">
        <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Timeline</p>
        <div className="flex items-center gap-2 text-xs text-slate-600">
          {COMPETITOR_HORIZONS.map((h, i) => (
            <span key={h.id} className="flex items-center gap-2">
              <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border ${HORIZON_STYLE[h.id]}`}>{h.label}</span>
              {i < COMPETITOR_HORIZONS.length - 1 && <ChevronRight className="w-3 h-3 text-slate-300" />}
            </span>
          ))}
        </div>
      </div>

      {/* Four zones */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {COMPETITOR_ZONES.map((zone) => {
          const zComps = competitors.filter((c) => c.zone === zone.id);
          const resetZone = () => {
            if (zComps.length === 0) return;
            if (window.confirm(`Reset "${zone.label}"? This will remove all ${zComps.length} entries in this zone.`)) {
              updateLandscape({ competitors: competitors.filter((c) => c.zone !== zone.id) });
              persist();
              toast.success(`${zone.label} reset`);
            }
          };
          return (
            <Card key={zone.id} data-testid={`cl-zone-${zone.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm text-slate-800">{zone.label}</CardTitle>
                  <div className="flex items-center gap-1">
                    {zComps.length > 0 && (
                      <Button size="sm" variant="ghost" onClick={resetZone} className="h-7 px-2 text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`cl-reset-${zone.id}-btn`} title="Reset this zone">
                        <RotateCcw className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => openNew(zone.id)} className="h-7 px-2 text-teal-600 hover:bg-teal-50" data-testid={`cl-add-${zone.id}-btn`}>
                      <PlusCircle className="w-4 h-4" /><span className="ml-1 text-xs">Add</span>
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-slate-500">{zone.hint}</p>
              </CardHeader>
              <CardContent>
                {zComps.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No entries yet.</p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {zComps.map((c) => (
                      <CompetitorCard key={c.id} c={c} onClick={() => openEdit(c)} onRemove={() => removeCompetitor(c.id)} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Differentiation claim */}
      <Card data-testid="cl-differentiation-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm text-slate-800">One-sentence differentiation claim</CardTitle>
            <div className="flex items-center gap-1">
              {claim.trim() && (
                <Button size="sm" variant="ghost" onClick={() => {
                  if (window.confirm("Clear the differentiation claim?")) {
                    updateLandscape({ differentiation_claim: "" });
                    persist();
                    toast.success("Differentiation claim cleared");
                  }
                }} className="h-7 px-2 text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid="cl-diff-reset-btn" title="Clear the claim">
                  <RotateCcw className="w-3.5 h-3.5" />
                </Button>
              )}
              <AiWand onClick={suggestDifferentiation} busy={diffBusy} testid="cl-diff-wand-btn" />
            </div>
          </div>
          <p className="text-xs text-slate-500">In 25–40 words, name the wedge that sets you apart against the competitors mapped above.</p>
        </CardHeader>
        <CardContent>
          <Textarea
            value={claim}
            onChange={(e) => updateLandscape({ differentiation_claim: e.target.value })}
            onBlur={() => persist()}
            rows={3}
            className="text-sm"
            placeholder="e.g. Only capsid-engineered AAV enabling intrathecal SMA delivery at 20× lower dose than Zolgensma, mitigating the systemic-safety wall that is now blocking approvals."
            data-testid="cl-differentiation-input"
          />
        </CardContent>
      </Card>

      {/* Editor dialog */}
      <CompetitorDialog open={!!editing} isNew={!!addingToZone} value={editing} onChange={setEditing} onClose={() => { setEditing(null); setAddingToZone(null); }} onSave={saveCompetitor} />
    </div>
  );
}

function CompetitorCard({ c, onClick, onRemove }) {
  return (
    <div
      onClick={onClick}
      data-testid={`cl-comp-${c.id}`}
      className="group cursor-pointer border rounded-lg p-3 bg-white hover:bg-slate-50 hover:shadow-sm transition-all"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800 truncate">{c.company}</p>
          <span className={`inline-block text-[9px] px-1.5 py-0.5 rounded border capitalize ${THREAT_STYLE[c.threat_level]}`}>{c.threat_level}</span>
          <span className={`inline-block text-[9px] px-1.5 py-0.5 rounded border ${HORIZON_STYLE[c.horizon]}`}>{COMPETITOR_HORIZONS.find((h) => h.id === c.horizon)?.label}</span>
        </div>
        <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className="text-slate-300 hover:text-red-500 flex-shrink-0 p-1" data-testid={`cl-remove-${c.id}`} title="Delete">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {(c.target || c.modality || c.stage) && (
        <p className="text-[11px] text-slate-500 mt-1 truncate">
          {[c.target, c.modality, c.stage].filter(Boolean).join(" · ")}
        </p>
      )}
      {c.why_matters && <p className="text-xs text-slate-600 mt-1 line-clamp-2">{c.why_matters}</p>}
    </div>
  );
}

function CompetitorDialog({ open, isNew, value, onChange, onClose, onSave }) {
  if (!value) return null;
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="cl-editor-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{isNew ? "Add competitor / threat" : "Edit competitor / threat"}</DialogTitle>
          <DialogDescription className="sr-only">Fill in the competitor details across the fields below.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Field label="Company name" required>
            <Input value={value.company} onChange={(e) => set({ company: e.target.value })} placeholder="e.g. Novartis - Zolgensma" data-testid="cl-field-company" autoFocus />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Zone">
              <Select value={value.zone} onValueChange={(v) => set({ zone: v })}>
                <SelectTrigger data-testid="cl-field-zone"><SelectValue /></SelectTrigger>
                <SelectContent>{COMPETITOR_ZONES.map((z) => <SelectItem key={z.id} value={z.id}>{z.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Horizon">
              <Select value={value.horizon} onValueChange={(v) => set({ horizon: v })}>
                <SelectTrigger data-testid="cl-field-horizon"><SelectValue /></SelectTrigger>
                <SelectContent>{COMPETITOR_HORIZONS.map((h) => <SelectItem key={h.id} value={h.id}>{h.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Target / MoA">
              <Input value={value.target} onChange={(e) => set({ target: e.target.value })} placeholder="e.g. SMN2 splicing" data-testid="cl-field-target" />
            </Field>
            <Field label="Modality">
              <Input value={value.modality} onChange={(e) => set({ modality: e.target.value })} placeholder="e.g. AAV gene therapy" data-testid="cl-field-modality" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Stage">
              <Input value={value.stage} onChange={(e) => set({ stage: e.target.value })} placeholder="e.g. approved / Ph2 / preclinical" data-testid="cl-field-stage" />
            </Field>
            <Field label="Threat level">
              <Select value={value.threat_level} onValueChange={(v) => set({ threat_level: v })}>
                <SelectTrigger data-testid="cl-field-threat"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Why they matter">
            <Textarea rows={2} value={value.why_matters} onChange={(e) => set({ why_matters: e.target.value })} className="text-sm" data-testid="cl-field-matters" />
          </Field>
          <Field label="Why they could win">
            <Textarea rows={2} value={value.why_win} onChange={(e) => set({ why_win: e.target.value })} className="text-sm" data-testid="cl-field-win" />
          </Field>
          <Field label="Why they could fail">
            <Textarea rows={2} value={value.why_fail} onChange={(e) => set({ why_fail: e.target.value })} className="text-sm" data-testid="cl-field-fail" />
          </Field>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={onSave} data-testid="cl-save-btn">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs font-medium text-slate-700 mb-1 block">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      {children}
    </div>
  );
}
